import { AIAnalysis } from "./ai.js";
import { Markdown } from "./markdown.js";
import AIUI from "./aiUI.js";
import d from "./DOM.js";
import Toast from "./toast.js";
import tradeJournal from "./tradeJournal.js";
import AccountManager from "./accountManager.js";
import { withCsrf } from "./csrf.js";

export default abstract class tradeData {
	private static userTags: string[] = [];

	public static async init() {
		d.e('click', tradeData.btnClickHandler, d.qsa<HTMLButtonElement>('.tradeDataBtn'));
		d.e('click', tradeData.toggleChildren, d.qsa<HTMLButtonElement>('.btn-expand'));
		d.e('change', tradeData.handleLimitChange, d.get<HTMLSelectElement>('limitSelect'));
		d.e('change', tradeData.handlePageChange, d.get<HTMLSelectElement>('pageSelect'));
		
		const limitSelect = d.get<HTMLSelectElement>('limitSelect');
		if (limitSelect) { limitSelect.value = new URLSearchParams(window.location.search).get('limit') || '10'; }
		
		// Load all tags for autocomplete
		await tradeData.loadAllTags();

		// Clear checkboxes on load to prevent browser caching issues
		const checkboxes = d.qsa<HTMLInputElement>('.trade-checkbox');
		checkboxes.forEach(cb => { cb.checked = false; });
		const selectAll = d.get<HTMLInputElement>('selectAll');
		
		if (selectAll) {
			selectAll.checked = false;
			selectAll.addEventListener('change', (e) => {
				const checkboxes = d.qsa<HTMLInputElement>('.trade-checkbox');
				checkboxes.forEach(cb => { cb.checked = (e.target as HTMLInputElement).checked; });
				tradeData.updateSelectionUI();
			});
		}

		// Selection Listeners
		const tbody_cb = d.qs<HTMLElement>('.journal-table tbody');
		if (tbody_cb) {
			tbody_cb.addEventListener('change', (e) => {
				if ((e.target as HTMLElement).classList.contains('trade-checkbox')) {
					tradeData.updateSelectionUI();
				}
			});
		}

		const clearSelectionBtn = d.get<HTMLButtonElement>('clear-selection-btn');
		if (clearSelectionBtn) {
			clearSelectionBtn.addEventListener('click', () => {
				const checkboxes = d.qsa<HTMLInputElement>('.trade-checkbox');
				checkboxes.forEach(cb => { cb.checked = false; });
				if (selectAll) { selectAll.checked = false; }
				tradeData.updateSelectionUI();
			});
		}
		
		tradeJournal.init();
		
		const analyzeBtn = d.get<HTMLButtonElement>('analyze-selected-btn');
		if (analyzeBtn) { analyzeBtn.addEventListener('click', tradeData.analyzeSelected); }
		
		const assignStrategyBtn = d.get<HTMLButtonElement>('assign-strategy-btn');
		if (assignStrategyBtn) { assignStrategyBtn.addEventListener('click', () => tradeData.openAssignStrategyModal(false)); }
		
		const assignAllStrategyBtn = d.get<HTMLButtonElement>('assign-all-strategy-btn');
		if (assignAllStrategyBtn) { assignAllStrategyBtn.addEventListener('click', () => tradeData.openAssignStrategyModal(true)); }
		
		const assignTagsBtn = d.get<HTMLButtonElement>('assign-tags-btn');
		if (assignTagsBtn) { assignTagsBtn.addEventListener('click', () => tradeData.openAssignTagsModal(false)); }

		const assignAllTagsBtn = d.get<HTMLButtonElement>('assign-all-tags-btn');
		if (assignAllTagsBtn) { assignAllTagsBtn.addEventListener('click', () => tradeData.openAssignTagsModal(true)); }

		// Bulk Actions Dropdown
		const bulkBtn = d.get<HTMLButtonElement>('bulk-actions-btn');
		const bulkMenu = d.get<HTMLElement>('bulk-menu');
		if (bulkBtn && bulkMenu) {
			bulkBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				bulkMenu.classList.toggle('open');
			});
			document.addEventListener('click', () => bulkMenu.classList.remove('open'));
		}

		// Export as… (Excel / CSV / PDF report) — exports exactly what the current filters show
		const exportBtn = d.get<HTMLButtonElement>('export-btn');
		const exportMenu = d.get<HTMLElement>('export-menu');
		if (exportBtn && exportMenu) {
			exportBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				exportMenu.classList.toggle('open');
			});
			document.addEventListener('click', () => exportMenu.classList.remove('open'));
			exportMenu.querySelectorAll<HTMLButtonElement>('[data-export]').forEach(btn =>
				btn.addEventListener('click', () => tradeData.exportAs(btn.dataset.export as 'xlsx' | 'csv' | 'pdf')));
		}

		const clearTagFilterBtn = d.get<HTMLButtonElement>('clear-tag-filter');
		if (clearTagFilterBtn) { clearTagFilterBtn.addEventListener('click', tradeData.clearTagFilter); }

		// Search Bar Autocomplete
		const searchInput = d.get<HTMLInputElement>('searchInput');
		if (searchInput) {
			tradeData.setupAutocomplete(searchInput, true);
			d.e('keyup', (e: KeyboardEvent) => { 
				if (e.key === 'Enter') { 
					const list = searchInput.parentElement?.querySelector('.suggestions-list');
					if (list?.classList.contains('visible')) { return; }
					tradeData.handleSearch(); 
				} 
			}, searchInput);
		}

		// Use delegation for tag actions
		const tbody = d.qs<HTMLElement>('.journal-table tbody');
		if (tbody) {
			tbody.addEventListener('click', (e: Event) => {
				const target = e.target as HTMLElement;
				
				if (target.classList.contains('tag-label')) {
					const parent = target.closest('.trade-tag') as HTMLElement;
					const tag = parent.getAttribute('data-tag');
					if (tag) { tradeData.filterByTag(tag); }
				} else if (target.classList.contains('remove-tag')) {
					const tag = target.getAttribute('data-tag');
					const ticket = target.getAttribute('data-ticket');
					if (tag && ticket) { tradeData.removeTagFromTrade(parseInt(ticket, 10), tag); }
				} else if (target.classList.contains('btn-add-tag')) {
					const ticket = target.getAttribute('data-ticket');
					if (ticket) { tradeData.promptAddTag(parseInt(ticket, 10)); }
				}
			});
		}

		const viewUnassignedBtn = d.get<HTMLButtonElement>('view-unassigned-btn');
		if (viewUnassignedBtn) { viewUnassignedBtn.addEventListener('click', tradeData.toggleUnassignedFilter); }
		
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get('unassigned') === 'true' && viewUnassignedBtn) {
			viewUnassignedBtn.classList.remove('btn-secondary');
			viewUnassignedBtn.classList.add('btn-primary');
			viewUnassignedBtn.innerText = 'Show All Trades';
		}

		const btnSearch = d.get<HTMLButtonElement>('btnSearch');
		if (btnSearch) { d.e('click', tradeData.handleSearch, btnSearch); }
		
		const btnClearSearch = d.get<HTMLButtonElement>('btnClearSearch');
		if (btnClearSearch) { d.e('click', tradeData.clearSearch, btnClearSearch); }
		
		const btnFilterDate = d.get<HTMLButtonElement>('btnFilterDate');
		if (btnFilterDate) { d.e('click', tradeData.handleDateFilter, btnFilterDate); }
		
		d.qsa<HTMLElement>('.sortable').forEach(th => {
			th.addEventListener('click', tradeData.handleSort);
			th.style.cursor = 'pointer';
		});
		
		tradeData.updateSortIcons();
	}

	private static async loadAllTags() {
		try {
			const res = await fetch('/api/trade-tags/all');
			if (res.ok) { tradeData.userTags = await res.json(); }
		} catch (e) {
			console.error('Failed to load user tags', e);
		}
	}

	private static setupAutocomplete(input: HTMLInputElement, isFilter: boolean) {
		const container = input.closest('.suggestions-container');
		if (!container) { return; }

		const list = container.querySelector('.suggestions-list') as HTMLElement;
		if (!list) { return; }

		let activeIndex = -1;

		input.addEventListener('input', () => {
			const val = input.value.trim().toLowerCase();
			if (!val) {
				list.classList.remove('visible');
				return;
			}

			// In assignment mode, handle comma separated
			const parts = val.split(',');
			const currentSearch = parts[parts.length - 1].trim();
			
			if (currentSearch.length < 1) {
				list.classList.remove('visible');
				return;
			}

			const matches = tradeData.userTags.filter(t => t.toLowerCase().includes(currentSearch)).slice(0, 8);
			
			if (matches.length > 0) {
				list.innerHTML = matches.map((m, i) => `<div class="suggestion-item" data-index="${i}">${m}</div>`).join('');
				list.classList.add('visible');
				activeIndex = -1;
			} else {
				list.classList.remove('visible');
			}
		});

		input.addEventListener('keydown', (e) => {
			if (!list.classList.contains('visible')) { return; }

			const items = list.querySelectorAll('.suggestion-item');

			if (e.key === 'ArrowDown') {
				e.preventDefault();
				activeIndex = (activeIndex + 1) % items.length;
				updateActive();
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				activeIndex = (activeIndex - 1 + items.length) % items.length;
				updateActive();
			} else if (e.key === 'Enter') {
				if (activeIndex >= 0) {
					e.preventDefault();
					selectItem(items[activeIndex].textContent || '');
				}
			} else if (e.key === 'Escape') {
				list.classList.remove('visible');
			}
		});

		list.addEventListener('click', (e) => {
			const target = e.target as HTMLElement;
			if (target.classList.contains('suggestion-item')) {
				selectItem(target.textContent || '');
			}
		});

		const updateActive = () => {
			list.querySelectorAll('.suggestion-item').forEach((el, i) => {
				el.classList.toggle('active', i === activeIndex);
			});
		};

		const selectItem = (val: string) => {
			if (isFilter) {
				// Search filter logic
				tradeData.filterByTag(val);
			} else {
				// Multi-tag assignment logic
				const parts = input.value.split(',');
				parts[parts.length - 1] = ' ' + val;
				input.value = parts.join(',').trim() + ', ';
				input.focus();
			}
			list.classList.remove('visible');
		};

		// Close on blur
		document.addEventListener('click', (e) => {
			if (e.target !== input && e.target !== list) {
				list.classList.remove('visible');
			}
		});
	}
	
	private static handleSort(e: Event) {
		const th = (e.target as HTMLElement).closest('th');
		if (!th) { return; }
		
		const sortCol = th.getAttribute('data-sort');
		if (!sortCol) { return; }
		
		const urlParams = new URLSearchParams(window.location.search);
		const currentSort = urlParams.get('sort') || 'time';
		const currentOrder = urlParams.get('order') || 'desc';
		
		let newOrder = 'desc';
		if (sortCol === currentSort) {
			newOrder = currentOrder === 'desc' ? 'asc' : 'desc';
		} else {
			newOrder = 'desc'; 
		}
		
		urlParams.set('sort', sortCol);
		urlParams.set('order', newOrder);
		window.location.search = urlParams.toString();
	}
	
	/** Filters for export, mirroring what the server rendered this page with. */
	private static exportParams(): URLSearchParams {
		const urlParams = new URLSearchParams(window.location.search);
		const params = new URLSearchParams();
		const account = urlParams.get('accountNumber') || AccountManager.currentAccount?.accountNumber || '';
		if (account) { params.set('accountNumber', account); }
		const start = d.get<HTMLInputElement>('startDate')?.value;
		const end = d.get<HTMLInputElement>('endDate')?.value;
		if (start) { params.set('startDate', start); }
		if (end) { params.set('endDate', end); }
		for (const key of ['search', 'tag', 'unassigned']) {
			const v = urlParams.get(key);
			if (v) { params.set(key, v); }
		}
		return params;
	}

	private static async exportAs(format: 'xlsx' | 'csv' | 'pdf') {
		const params = tradeData.exportParams();

		if (format === 'pdf') {
			// The report is account-level performance for the date range (search/tag filters
			// don't apply to account statistics).
			const report = new URLSearchParams();
			['accountNumber', 'startDate', 'endDate'].forEach(k => { const v = params.get(k); if (v) { report.set(k, v); } });
			window.open(`/report?${report.toString()}`, '_blank', 'noopener');
			return;
		}

		params.set('format', format);
		try {
			Toast.show('Preparing export…', 'info', 2000);
			const res = await fetch(`/api/trade-data/export?${params.toString()}`);
			if (!res.ok) { throw new Error(`HTTP ${res.status}`); }
			const blob = await res.blob();
			const disposition = res.headers.get('Content-Disposition') || '';
			const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || `tradevision-trades.${format}`;
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(url), 10000);
		} catch (err) {
			console.error('Export failed', err);
			Toast.show('Export failed — please try again.', 'error');
		}
	}

	private static updateSortIcons() {
		const urlParams = new URLSearchParams(window.location.search);
		const currentSort = urlParams.get('sort') || 'time';
		const currentOrder = urlParams.get('order') || 'desc';
		const icon = d.qs<HTMLElement>(`.sort-icon[data-col="${currentSort}"]`);
		
		if (icon) {
			icon.innerHTML = currentOrder === 'asc' ? ' &#9650;' : ' &#9660;';
		}
	}

	private static updateSelectionUI() {
		const checkboxes = d.qsa<HTMLInputElement>('.trade-checkbox');
		const selectedCount = Array.from(checkboxes).filter(cb => cb.checked).length;
		
		const toolbar = d.get<HTMLElement>('selection-toolbar');
		const countLabel = d.get<HTMLElement>('selected-count');
		
		if (toolbar && countLabel) {
			if (selectedCount > 0) {
				countLabel.innerText = selectedCount.toString();
				toolbar.classList.add('visible');
			} else {
				toolbar.classList.remove('visible');
			}
		}
	}
	
	private static handleSearch() {
		const searchInput = d.get<HTMLInputElement>('searchInput');
		const search = searchInput.value.trim();
		const urlParams = new URLSearchParams(window.location.search);
		
		if (search) { urlParams.set('search', search); }
		else { urlParams.delete('search'); }
		
		urlParams.set('page', '1');
		
		window.location.search = urlParams.toString();
	}
	
	private static handleDateFilter() {
		const startDate = d.get<HTMLInputElement>('startDate').value;
		const endDate = d.get<HTMLInputElement>('endDate').value;
		const urlParams = new URLSearchParams(window.location.search);
		
		if (startDate) { urlParams.set('startDate', startDate); }
		if (endDate) { urlParams.set('endDate', endDate); }
		
		urlParams.set('page', '1');
		window.location.search = urlParams.toString();
	}
	
	private static clearSearch() {
		const urlParams = new URLSearchParams(window.location.search);
		urlParams.delete('search');
		urlParams.set('page', '1');
		window.location.search = urlParams.toString();
	}

	private static clearTagFilter() {
		const urlParams = new URLSearchParams(window.location.search);
		urlParams.delete('tag');
		urlParams.set('page', '1');
		window.location.search = urlParams.toString();
	}

	private static filterByTag(tag: string) {
		const urlParams = new URLSearchParams(window.location.search);
		urlParams.set('tag', tag);
		urlParams.set('page', '1');
		window.location.search = urlParams.toString();
	}

	private static async promptAddTag(ticket: number) {
		const tag = prompt('Enter tag name:');
		if (!tag) { return; }

		try {
			const res = await fetch('/api/trade-tags/add', {
				method: 'POST',
				headers: withCsrf({ 'Content-Type': 'application/json' }),
				body: JSON.stringify({ ticket, tag })
			});

			if (res.ok) {
				Toast.show('Tag added!', 'success');
				window.location.reload();
			} else {
				Toast.show('Failed to add tag.', 'error');
			}
		} catch (e) {
			console.error(e);
		}
	}

	private static async removeTagFromTrade(ticket: number, tag: string) {
		if (!confirm(`Are you sure you want to remove the tag "${tag}"?`)) { return; }

		try {
			const res = await fetch('/api/trade-tags/remove', {
				method: 'POST',
				headers: withCsrf({ 'Content-Type': 'application/json' }),
				body: JSON.stringify({ ticket, tag })
			});

			if (res.ok) {
				Toast.show('Tag removed!', 'success');
				window.location.reload();
			} else {
				Toast.show('Failed to remove tag.', 'error');
			}
		} catch (e) {
			console.error(e);
		}
	}

	private static async openAssignTagsModal(assignAll: boolean) {
		let selectedTickets: number[] = [];

		if (!assignAll) {
			const checkboxes = d.qsa<HTMLInputElement>('.trade-checkbox');
			selectedTickets = Array.from(checkboxes).filter(cb => cb.checked).map(cb => parseInt(cb.getAttribute('data-ticket') || '0', 10));

			if (selectedTickets.length === 0) {
				Toast.show('Please select at least one trade.', 'error');
				return;
			}
		} else {
			if (!confirm('Are you sure you want to assign tags to ALL filtered trades?')) {
				return;
			}
		}

		// Create Modal
		const modal = document.createElement('div');
		modal.style.position = 'fixed';
		modal.style.top = '0';
		modal.style.left = '0';
		modal.style.width = '100%';
		modal.style.height = '100%';
		modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
		modal.style.display = 'flex';
		modal.style.justifyContent = 'center';
		modal.style.alignItems = 'center';
		modal.style.zIndex = '1000';

		const content = document.createElement('div');
		content.style.backgroundColor = 'var(--theme-bg-panel)';
		content.style.padding = '20px';
		content.style.borderRadius = '8px';
		content.style.width = '350px';
		content.style.display = 'flex';
		content.style.flexDirection = 'column';
		content.style.gap = '15px';

		const title = document.createElement('h3');
		const accountLabel = AccountManager.currentAccount ? ` (${AccountManager.currentAccount.accountNumber})` : '';
		title.innerText = assignAll ? `Assign Tags to ALL trades${accountLabel}` : `Assign Tags (${selectedTickets.length} selected)`;
		title.style.margin = '0';

		// Input with Autocomplete container
		const container = document.createElement('div');
		container.className = 'suggestions-container';
		
		const input = document.createElement('input');
		input.type = 'text';
		input.className = 'form-control';
		input.placeholder = 'Tags (comma separated)...';
		
		const list = document.createElement('div');
		list.className = 'suggestions-list';
		
		container.appendChild(input);
		container.appendChild(list);

		const actionSelect = document.createElement('select');
		actionSelect.className = 'form-control';
		actionSelect.innerHTML = `
			<option value="add">Add to existing tags</option>
			<option value="replace">Replace existing tags</option>
			<option value="remove">Remove these tags</option>
		`;

		const btnContainer = document.createElement('div');
		btnContainer.style.display = 'flex';
		btnContainer.style.justifyContent = 'flex-end';
		btnContainer.style.gap = '10px';

		const cancelBtn = document.createElement('button');
		cancelBtn.innerText = 'Cancel';
		cancelBtn.className = 'btn-secondary';
		cancelBtn.onclick = () => { document.body.removeChild(modal); }

		const saveBtn = document.createElement('button');
		saveBtn.innerText = 'Save';
		saveBtn.className = 'btn-primary';

		saveBtn.onclick = async () => {
			const tagsStr = input.value.trim();
			if (!tagsStr) {
				Toast.show('Please enter at least one tag.', 'error');
				return;
			}

			const tags = tagsStr.split(',').map(t => t.trim()).filter(t => t);
			if (tags.length === 0) {
				Toast.show('Please enter at least one tag.', 'error');
				return;
			}

			saveBtn.disabled = true;
			saveBtn.innerText = 'Saving...';

			try {
				const res = await fetch('/api/trade-tags/bulk', {
					method: 'POST',
					headers: withCsrf({ 'Content-Type': 'application/json' }),
					body: JSON.stringify({
						tickets: assignAll ? [] : selectedTickets,
						tags,
						assignAll,
						action: actionSelect.value,
						accountNumber: AccountManager.currentAccount?.accountNumber
					})
				});

				if (res.ok) {
					Toast.show('Tags updated successfully!', 'success');
					document.body.removeChild(modal);
					setTimeout(() => window.location.reload(), 1500);
				} else {
					Toast.show('Failed to update tags.', 'error');
				}
			} catch (e) {
				console.error(e);
				Toast.show('An error occurred.', 'error');
			} finally {
				saveBtn.disabled = false;
				saveBtn.innerText = 'Save';
			}
		};

		btnContainer.appendChild(cancelBtn);
		btnContainer.appendChild(saveBtn);

		content.appendChild(title);
		content.appendChild(container);
		content.appendChild(actionSelect);
		content.appendChild(btnContainer);
		modal.appendChild(content);
		document.body.appendChild(modal);

		// Setup autocomplete for the modal input
		tradeData.setupAutocomplete(input, false);
	}
	
	private static async openAssignStrategyModal(assignAll: boolean) {
		let selectedTickets: number[] = [];
		
		if (!assignAll) {
			const checkboxes = d.qsa<HTMLInputElement>('.trade-checkbox');
			selectedTickets = Array.from(checkboxes).filter(cb => cb.checked).map(cb => parseInt(cb.getAttribute('data-ticket') || '0', 10));
			
			if (selectedTickets.length === 0) {
				Toast.show('Please select at least one trade.', 'error');
				return;
			}
		} else {
			if (!confirm('Are you sure you want to assign a strategy to ALL filtered trades? This will overwrite existing strategies.')) {
				return;
			}
		}
		
		let strategies: any[] = [];
		
		try {
			const res = await fetch('/api/strategies');
			if (res.ok) { strategies = await res.json(); }
		} catch (e) {
			console.error(e);
			Toast.show('Failed to load strategies', 'error');
			return;
		}
		
		const modal = document.createElement('div');
		modal.style.position = 'fixed';
		modal.style.top = '0';
		modal.style.left = '0';
		modal.style.width = '100%';
		modal.style.height = '100%';
		modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
		modal.style.display = 'flex';
		modal.style.justifyContent = 'center';
		modal.style.alignItems = 'center';
		modal.style.zIndex = '1000';
		
		const content = document.createElement('div');
		content.style.backgroundColor = 'var(--theme-bg-panel)';
		content.style.padding = '20px';
		content.style.borderRadius = '8px';
		content.style.width = '300px';
		content.style.display = 'flex';
		content.style.flexDirection = 'column';
		content.style.gap = '15px';
		
		const title = document.createElement('h3');
		const accountLabel = AccountManager.currentAccount ? ` (${AccountManager.currentAccount.accountNumber})` : '';
		title.innerText = assignAll ? `Assign Strategy to ALL trades${accountLabel}` : `Assign Strategy (${selectedTickets.length} selected)`;
		title.style.margin = '0';
		
		const select = document.createElement('select');
		select.className = 'form-control';
		select.innerHTML = '<option value="">Select Strategy...</option>';
		
		const system = strategies.filter(s => s.type === 'SYSTEM');
		const user = strategies.filter(s => s.type === 'USER');
		
		if (system.length) {
			const group = document.createElement('optgroup');
			group.label = 'System Strategies';
			
			system.forEach(s => {
				const opt = document.createElement('option');
				opt.value = s.id;
				opt.textContent = s.label;
				group.appendChild(opt);
			});
			
			select.appendChild(group);
		}
		
		if (user.length) {
			const group = document.createElement('optgroup');
			group.label = 'My Strategies';
			
			user.forEach(s => {
				const opt = document.createElement('option');
				opt.value = s.id;
				opt.textContent = s.label;
				group.appendChild(opt);
			});
			
			select.appendChild(group);
		}
		
		const btnContainer = document.createElement('div');
		btnContainer.style.display = 'flex';
		btnContainer.style.justifyContent = 'flex-end';
		btnContainer.style.gap = '10px';
		
		const cancelBtn = document.createElement('button');
		cancelBtn.innerText = 'Cancel';
		cancelBtn.className = 'btn-secondary';
		cancelBtn.onclick = () => { document.body.removeChild(modal); }
		
		const saveBtn = document.createElement('button');
		saveBtn.innerText = 'Save';
		saveBtn.className = 'btn-primary';
		
		saveBtn.onclick = async () => {
			const strategy = select.value;
			
			if (!strategy) {
				Toast.show('Please select a strategy', 'error');
				return;
			}
			
			saveBtn.disabled = true;
			saveBtn.innerText = 'Saving...';
			
			try {
				const res = await fetch('/api/trade-journal/bulk-strategy', {
					method: 'POST',
					headers: withCsrf({ 'Content-Type': 'application/json' }),
					body: JSON.stringify({ 
						tickets: assignAll ? [] : selectedTickets, 
						strategy,
						assignAll,
						accountNumber: AccountManager.currentAccount?.accountNumber
					})
				});
				
				if (res.ok) {
					Toast.show('Strategies updated successfully!', 'success');
					document.body.removeChild(modal);
					setTimeout(() => window.location.reload(), 1500);
				} else {
					Toast.show('Failed to update strategies.', 'error');
				}
			} catch (e) {
				console.error(e);
				Toast.show('An error occurred.', 'error');
			} finally {
				saveBtn.disabled = false;
				saveBtn.innerText = 'Save';
			}
		};
		
		btnContainer.appendChild(cancelBtn);
		btnContainer.appendChild(saveBtn);
		
		content.appendChild(title);
		content.appendChild(select);
		content.appendChild(btnContainer);
		modal.appendChild(content);
		document.body.appendChild(modal);
	}
	
	private static async analyzeSelected() {
		const checkboxes = d.qsa<HTMLInputElement>('.trade-checkbox');
		const selectedTickets = Array.from(checkboxes).filter(cb => cb.checked).map(cb => parseInt(cb.getAttribute('data-ticket') || '0', 10));
		
		if (selectedTickets.length === 0) {
			Toast.show('Please select at least one trade to analyze.', 'error');
			return;
		}
		
		const modal = document.createElement('div');
		modal.style.position = 'fixed';
		modal.style.top = '0';
		modal.style.left = '0';
		modal.style.width = '100%';
		modal.style.height = '100%';
		modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
		modal.style.display = 'flex';
		modal.style.justifyContent = 'center';
		modal.style.alignItems = 'center';
		modal.style.zIndex = '1000';
		
		const content = document.createElement('div');
		content.style.backgroundColor = 'var(--theme-bg-panel)';
		content.style.padding = '20px';
		content.style.borderRadius = '8px';
		content.style.width = '80%';
		content.style.maxWidth = '800px';
		content.style.maxHeight = '80%';
		content.style.overflowY = 'auto';
		content.style.position = 'relative';
		
		const closeBtn = document.createElement('button');
		closeBtn.innerText = 'Close';
		closeBtn.className = 'btn-secondary';
		closeBtn.style.position = 'absolute';
		closeBtn.style.top = '10px';
		closeBtn.style.right = '10px';
		closeBtn.onclick = () => { document.body.removeChild(modal); }
		
		const title = document.createElement('h2');
		title.innerText = 'AI Trade Analysis';
		
		const body = document.createElement('div');
		body.className = 'markdown-body';
		AIUI.showLoading(body);
		
		content.appendChild(closeBtn);
		content.appendChild(title);
		content.appendChild(body);
		modal.appendChild(content);
		document.body.appendChild(modal);
		
		try {
			const result = await AIAnalysis.analyzeTrades(selectedTickets);
			
			if (result.ok === false && result.payload?.reason?.includes('403')) {
				 body.innerHTML = AIUI.getUpgradeMessage();
				 return;
			}
			
			const data = result.payload || result;
			
			if (data.analysis) { body.innerHTML = Markdown.parse(data.analysis); }
			else { body.innerText = 'No analysis returned.'; }
		} catch (e) {
			body.innerText = 'Error analyzing trades.';
		}
	}
	
	private static btnClickHandler(e: MouseEvent) {
		d.action(<EventTarget>e.target, '.tradeDataBtn', action => {
			if (action == 'prev') { tradeData.goPrevPage(); }
			else if (action == 'next') { tradeData.goNextPage(); }
			else if (action == 'first') { tradeData.goFirstPage(); }
			else if (action == 'last') { tradeData.goLastPage(); }
		});
	}
	
	private static toggleUnassignedFilter() {
		const urlParams = new URLSearchParams(window.location.search);
		const current = urlParams.get('unassigned') === 'true';
		
		if (current) {
			urlParams.delete('unassigned');
		} else {
			urlParams.set('unassigned', 'true');
			urlParams.set('page', '1');
		}
		
		window.location.search = urlParams.toString();
	}
	
	private static handleLimitChange() {
		const limitSelect = d.get<HTMLSelectElement>('limitSelect');
		const limit = limitSelect ? parseInt(limitSelect.value, 10) : 10;
		
		const urlParams = new URLSearchParams(window.location.search);
		urlParams.set('limit', limit.toString());
		urlParams.set('page', '1');
		
		window.location.search = urlParams.toString();
	}
	
	private static handlePageChange() {
		const pageSelect = d.get<HTMLSelectElement>('pageSelect');
		const page = pageSelect ? pageSelect.value : '1';
		const urlParams = new URLSearchParams(window.location.search);
		
		urlParams.set('page', page);
		window.location.search = urlParams.toString();
	}
	
	private static goFirstPage() {
		const urlParams = new URLSearchParams(window.location.search);
		urlParams.set('page', '1');
		window.location.search = urlParams.toString();
	}
	
	private static goLastPage() {
		const urlParams = new URLSearchParams(window.location.search);
		const totalPages = d.get<HTMLInputElement>('totalPages')?.value || '1';
		urlParams.set('page', totalPages);
		window.location.search = urlParams.toString();
	}
		
	private static goNextPage() {
		const urlParams = new URLSearchParams(window.location.search);
		const currentPage = parseInt(urlParams.get('page') || '1', 10);
		const totalPages = parseInt(d.qs<HTMLInputElement>('#totalPages')?.value || '1', 10);
		
		if (currentPage >= totalPages) { return; }
		
		urlParams.set('page', (currentPage + 1).toString());
		window.location.search = urlParams.toString();
	}
	
	private static goPrevPage() {
		const urlParams = new URLSearchParams(window.location.search);
		const currentPage = parseInt(urlParams.get('page') || '1', 10);
		
		if (currentPage > 1) {
			urlParams.set('page', (currentPage - 1).toString());
			window.location.search = urlParams.toString();
		}
	}
	
	private static async toggleChildren(e: Event) {
		const btn = e.target as HTMLButtonElement;
		const positionID = btn.getAttribute('data-position');
		if (!positionID) { return; }
		
		const parentRow = btn.closest('tr');
		if (!parentRow) { return; }
		
		const table = parentRow.closest('table');
		if (!table) { return; }
		
		const childRows = Array.from(table.querySelectorAll(`tr.child-row[data-position="${positionID}"]`)) as HTMLTableRowElement[];
		
		if (childRows.length > 0) {
			const isHidden = childRows[0].style.display === 'none';
			childRows.forEach(row => { row.style.display = isHidden ? 'table-row' : 'none'; });
			btn.innerText = isHidden ? '-' : '+';
		} else {
			try {
				const res = await fetch(`/api/trade-data/children?positionID=${positionID}`);
				
				if (res.ok) {
					const html = await res.text();
					
					if (html.trim().length > 0) {
						parentRow.insertAdjacentHTML('afterend', html);
						const newChildRows = Array.from(table.querySelectorAll(`tr.child-row[data-position="${positionID}"]`)) as HTMLTableRowElement[];
						
						newChildRows.forEach(row => {
							const journalBtns = row.querySelectorAll('.journalBtn');
							journalBtns.forEach(b => { b.addEventListener('click', (e) => tradeData.btnClickHandler(e as MouseEvent)); });
						});
						
						btn.innerText = '-';
					} else {
						console.warn('No children returned for position', positionID);
					}
				}
			} catch (err) {
				console.error(err);
				return;
			}
		}
	}
}
